import {
  Alert,
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Radio,
  Space,
  Spin,
  Tabs,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { BEHAVIOUR_LABELS } from '../../../common/constants';

const formatUser = (user) =>
  user ? `${user.firstName || ''} (${user.email || ''})`.trim() : null;

const formatDateTime = (value) =>
  value ? dayjs(value).format('DD MMM YYYY, hh:mm A') : null;

const employeeNameOf = (detail) =>
  detail?.employee
    ? `${detail.employee.firstName || ''} ${detail.employee.lastName || ''
      }`.trim()
    : null;

/** Read-only rows shown before the stats block: who logged the incident,
 * then what it is and who it's about. */
function buildHeaderItems(detail) {
  return [
    {
      key: 'loggedBy',
      label: 'Reported By',
      value: formatUser(detail?.loggedByUser),
    },
    {
      key: 'loggedAt',
      label: 'Reported At',
      value: formatDateTime(detail?.loggedByUser?.loggedAt),
    },
    {
      key: 'behaviour',
      label: 'Behaviour',
      value: BEHAVIOUR_LABELS[detail?.behaviourKey] || detail?.behaviourKey,
    },
    { key: 'employee', label: 'Employee', value: employeeNameOf(detail) },
  ].filter((item) => item.value);
}

/** Read-only rows shown for reason, excuse, and archive status. Falsy
 * values (missing reason/excuse/archive name) are dropped so e.g. "Genuine
 * By" only shows once there's actually an excuse on record. */
function buildReasonItems(detail) {
  return [
    { key: 'reason', label: 'Reason', value: detail?.reason },
    {
      key: 'isExcused',
      label: 'Genuine',
      value: detail?.isExcused ? 'Yes' : 'No',
    },
    {
      key: 'excusedBy',
      label: 'Genuine By',
      value: formatUser(detail?.excusedByUser),
    },
    {
      key: 'excusedAt',
      label: 'Genuine At',
      value: formatDateTime(detail?.excusedByUser?.excusedAt),
    },
    {
      key: 'excuseReason',
      label: 'Genuine Reason',
      value: detail?.excuseReason,
    },
    {
      key: 'isArchived',
      label: 'Archived',
      value: detail?.isArchived ? 'Yes' : 'No',
    },
    {
      key: 'archivedBy',
      label: 'Archived By',
      value: formatUser(detail?.archivedByUser),
    },
    {
      key: 'archivedAt',
      label: 'Archived At',
      value: formatDateTime(detail?.archivedByUser?.archivedAt),
    },
    {
      key: 'archiveReason',
      label: 'Archive Reason',
      value: detail?.archiveReason,
    },
  ].filter((item) => item.value);
}

/**
 * ManageIncidentModal shows an incident's full details (fetched via
 * getIncidentDetails). When `disabled` (no update permission) it's
 * view-only — just the details and a Close button. Otherwise it also shows
 * the Genuine/Archive edit tabs; they're mutually exclusive per save — the
 * active tab is the only one submitted, so a save can never touch both at
 * once. On both tabs the Reason field only appears once the toggle actually
 * diverges from the saved value — nothing to justify while it sits unchanged.
 * On the Genuine tab a pending change is required either way (optional while
 * marking genuine, required while removing it); on the Archive tab only a
 * pending archive (Yes) needs one at all.
 */
function ManageIncidentModal({
  open,
  loading,
  incident,
  detail,
  detailLoading,
  onSave,
  onCancel,
  form,
  disabled,
}) {
  const [activeTab, setActiveTab] = useState('excuse');

  const watchedIsExcused = Form.useWatch('isExcused', form);
  const watchedIsArchived = Form.useWatch('isArchived', form);

  // The excuse/archive mutations error when saved with no actual change
  // (e.g. "Incident is not archived" on a same-state archive save), and that
  // check is on the boolean state alone — so Save must stay disabled until
  // the Yes/No toggle itself diverges from what's on record. Editing the
  // reason text alone (with the toggle left at its saved value) can never be
  // a valid submission on its own.
  const isExcuseChanged = watchedIsExcused !== (incident?.isExcused ?? false);
  const isArchiveChanged =
    watchedIsArchived !== (incident?.isArchived ?? false);
  // Both Reason fields only matter once there's something to justify — stay
  // hidden while the toggle sits at its saved value (nothing pending), and
  // appear only once the user actually flips it. On the Genuine tab that can
  // go either way (optional while marking genuine, required while removing
  // it); on the Archive tab only archiving (Yes) needs a reason at all.
  const showExcuseReason = isExcuseChanged;
  const showArchiveReason = watchedIsArchived && isArchiveChanged;
  // The BE rejects excusing an archived incident
  // (ATTENDANCE_INCIDENT_ARCHIVED) — it must be unarchived first. Block the
  // Genuine tab while the incident is archived on record.
  const isArchived = incident?.isArchived ?? false;
  const isSaveDisabled =
    activeTab === 'excuse'
      ? !isExcuseChanged || isArchived
      : !isArchiveChanged;

  useEffect(() => {
    if (open && !disabled) {
      setActiveTab('excuse');
      form.setFieldsValue({
        isExcused: incident?.isExcused ?? false,
        excuseReason: incident?.excuseReason,
        isArchived: incident?.isArchived ?? false,
        archiveReason: incident?.archiveReason,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, disabled, incident]);

  // Clear any typed reason once its field goes hidden again (BE returns
  // null for reason in that state anyway), so a stale value from before the
  // toggle flipped back isn't submitted.
  useEffect(() => {
    if (!showArchiveReason) {
      form.setFieldValue('archiveReason', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchiveReason]);

  useEffect(() => {
    if (!showExcuseReason) {
      form.setFieldValue('excuseReason', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExcuseReason]);

  const handleFinish = (values) => {
    onSave({ ...values, action: activeTab });
  };

  const headerItems = buildHeaderItems(detail);
  const reasonItems = buildReasonItems(detail);

  return (
    <Modal
      title="Incident Details"
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      className="excuse-incident-modal"
      afterClose={() => form.resetFields()}
    >
      <Spin spinning={detailLoading}>
        <div className="incident-details-scroll">
          {headerItems.length > 0 && (
            <Descriptions
              column={1}
              size="small"
              bordered
              className="incident-details-descriptions"
            >
              {headerItems.map((item) => (
                <Descriptions.Item key={item.key} label={item.label}>
                  {item.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          )}

          {reasonItems.length > 0 && (
            <Descriptions
              column={1}
              size="small"
              bordered
              className="incident-details-descriptions"
            >
              {reasonItems.map((item) => (
                <Descriptions.Item key={item.key} label={item.label}>
                  {item.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          )}
        </div>
      </Spin>

      {disabled ? (
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Button onClick={onCancel}>Close</Button>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          style={{ marginTop: 16 }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            destroyInactiveTabPane
            items={[
              {
                key: 'excuse',
                label: 'Genuine',
                children: (
                  <>
                    {isArchived && (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="This incident is archived. Unarchive it from the Archive tab before marking genuine."
                      />
                    )}
                    <Form.Item
                      name="isExcused"
                      label="Mark this incident as genuine?"
                      rules={[
                        { required: true, message: 'Please select an option' },
                      ]}
                    >
                      <Radio.Group disabled={isArchived}>
                        <Radio value>Yes</Radio>
                        <Radio value={false}>No</Radio>
                      </Radio.Group>
                    </Form.Item>
                    {showExcuseReason && (
                      <Form.Item
                        name="excuseReason"
                        label="Reason"
                        rules={[
                          {
                            required: watchedIsExcused === false,
                            message:
                              'Please enter reason for removing genuine',
                          },
                          {
                            max: 500,
                            message: 'Reason cannot exceed 500 characters',
                          },
                        ]}
                      >
                        <Input.TextArea
                          rows={3}
                          maxLength={500}
                          placeholder={
                            watchedIsExcused === false
                              ? 'Enter reason for removing genuine'
                              : 'Enter reason (optional)'
                          }
                        />
                      </Form.Item>
                    )}
                  </>
                ),
              },
              {
                key: 'archive',
                label: 'Archive',
                children: (
                  <>
                    <Form.Item
                      name="isArchived"
                      label="Archive this incident?"
                      rules={[
                        { required: true, message: 'Please select an option' },
                      ]}
                    >
                      <Radio.Group>
                        <Radio value>Yes</Radio>
                        <Radio value={false}>No</Radio>
                      </Radio.Group>
                    </Form.Item>
                    {showArchiveReason && (
                      <Form.Item
                        name="archiveReason"
                        label="Archive Reason"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter archive reason',
                          },
                          {
                            max: 500,
                            message: 'Reason cannot exceed 500 characters',
                          },
                        ]}
                      >
                        <Input.TextArea
                          rows={3}
                          maxLength={500}
                          placeholder="Enter reason for archiving"
                        />
                      </Form.Item>
                    )}
                  </>
                ),
              },
            ]}
          />

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={isSaveDisabled}
              >
                Save
              </Button>
              <Button onClick={onCancel}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

export default ManageIncidentModal;
